import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Typography, Radio, Checkbox, Flex, theme } from "antd";
import {
  UserAddOutlined,
  ArrowLeftOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useSignUp } from "../hooks/useSignUp";
import { useTypesUsers } from "../../typesUsers/hooks/userTypesUsers";
import type { SignInFormData } from "../types/auth";
import openNotification from '../../../utils/openNotification';

const dontShowTypeUsers = [1];

const SignUpPage = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const signInMutation = useSignUp();
  const { data: typesUserData, isLoading: isLoadingTypeUsers } =
    useTypesUsers();

  const onFinish = (values: SignInFormData) => {
    signInMutation.mutate(values, {
      onSuccess: () => {
        //alert("Registro exitoso");
        navigate("/dashboard");
      },
      onError: (error: any) => {
        openNotification('error', `Error - ${error?.status || '500'}`, error.message || 'Error al registrarse');
      },
    });
  };

  const handleBack = () => {
    navigate("/login");
  };

  if (isLoadingTypeUsers) {
    return <div>Loading...</div>;
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "28rem",
        margin: "0 auto",
        minHeight: "100vh",
        background: token.colorBgContainer,
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        position: "relative",
      }}
    >
      <Flex
        justify="space-between"
        align="center"
        style={{
          padding: "16px",
          position: "sticky",
          top: 0,
          background: (token as any).customBgBlur || "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(4px)",
          zIndex: 10,
        }}
      >
        <Flex
          align="center"
          justify="center"
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
          }}
        >
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          />
        </Flex>
        <div style={{ width: "40px" }} />
      </Flex>

      <Flex
        vertical
        style={{
          flex: 1,
          padding: "24px 24px 32px",
        }}
      >
        <div style={{ marginBottom: "32px" }}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(16, 185, 129, 0.1)",
              borderRadius: "12px",
              color: token.colorSuccess,
              marginBottom: "24px",
            }}
          >
            <UserAddOutlined style={{ fontSize: "24px" }} />
          </Flex>
          <Typography.Title
            level={1}
            style={{
              fontSize: "32px",
              fontWeight: "extrabold",
              lineHeight: "1.25",
              letterSpacing: "tight",
              marginBottom: "8px",
            }}
          >
            Registro de Usuario
          </Typography.Title>
          <Typography.Text
            style={{
              fontSize: "16px",
              color: token.colorTextSecondary,
              lineHeight: "normal",
            }}
          >
            Crea tu cuenta para gestionar facturas y contratos.
          </Typography.Text>
        </div>

        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item
            name="name"
            label="Nombre (s)"
            rules={[{ required: true, message: "Campo requerido" }]}
          >
            <Input
              placeholder="Ej. Alejandro"
              size="large"
              style={{
                borderRadius: "12px",
                backgroundColor: token.colorBgLayout,
                borderColor: token.colorBorder,
              }}
            />
          </Form.Item>
          <Form.Item
            name="lastname"
            label="Apellido paterno"
            rules={[{ required: true, message: "Campo requerido" }]}
          >
            <Input
              placeholder="Ej. Sanchez"
              size="large"
              style={{
                borderRadius: "12px",
                backgroundColor: token.colorBgLayout,
                borderColor: token.colorBorder,
              }}
            />
          </Form.Item>
          <Form.Item name="lastname_2" label="Apellido materno">
            <Input
              placeholder="Ej. Perez"
              size="large"
              style={{
                borderRadius: "12px",
                backgroundColor: token.colorBgLayout,
                borderColor: token.colorBorder,
              }}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Correo electrónico"
            rules={[
              { required: true, message: "Campo requerido" },
              { type: "email", message: "Email inválido" },
            ]}
          >
            <Input
              placeholder="nombre@empresa.com"
              size="large"
              style={{
                borderRadius: "12px",
                backgroundColor: token.colorBgLayout,
                borderColor: token.colorBorder,
              }}
            />
          </Form.Item>

          <Form.Item
            name="id_type_user"
            rules={[{ required: true, message: "Selecciona un rol" }]}
            label="Tipo de cuenta"
          >
            <Radio.Group size="large" style={{ width: "100%" }}>
              <Flex gap={12}>
                {typesUserData?.map((typeUser) => {
                  if (dontShowTypeUsers.includes(typeUser.no_type_user))
                    return null;
                  return (
                    <Radio.Button
                      key={typeUser.id_type_user}
                      value={typeUser.id_type_user}
                      style={{
                        height: "48px",
                        borderRadius: "12px",
                        textAlign: "center",
                        fontWeight: "medium",
                        flex: 1,
                      }}
                    >
                      {typeUser.type_user}
                    </Radio.Button>
                  );
                })}
              </Flex>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="password"
            label="Contraseña"
            rules={[
              { required: true, message: "Campo requerido" },
              { min: 6, message: "Mínimo 6 caracteres" },
            ]}
          >
            <Input.Password
              placeholder="••••••••"
              size="large"
              iconRender={(visible) =>
                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
              style={{
                borderRadius: "12px",
                backgroundColor: token.colorBgLayout,
                borderColor: token.colorBorder,
              }}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirmar contraseña"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Campo requerido" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Las contraseñas no coinciden"),
                  );
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="••••••••"
              size="large"
              iconRender={(visible) =>
                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
              style={{
                borderRadius: "12px",
                backgroundColor: token.colorBgLayout,
                borderColor: token.colorBorder,
              }}
            />
          </Form.Item>

          <Form.Item
            name="terms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error("Debes aceptar los términos")),
              },
            ]}
          >
            <Checkbox>
              <Typography.Text style={{ fontSize: "14px", color: token.colorTextSecondary }}>
                Al registrarte, aceptas nuestros{" "}
                <Typography.Link
                  href="#"
                  style={{ fontWeight: "bold", color: token.colorSuccess }}
                >
                  Términos y Condiciones
                </Typography.Link>{" "}
                y la{" "}
                <Typography.Link
                  href="#"
                  style={{ fontWeight: "bold", color: token.colorSuccess }}
                >
                  Política de Privacidad
                </Typography.Link>
                .
              </Typography.Text>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={signInMutation.isPending}
              style={{
                width: "100%",
                height: "56px",
                fontSize: "16px",
                fontWeight: "bold",
                borderRadius: "12px",
                backgroundColor: token.colorSuccess,
                borderColor: token.colorSuccess,
                boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.1)",
              }}
            >
              Registrarse
            </Button>
          </Form.Item>
        </Form>

        <div
          style={{
            marginTop: "auto",
            paddingTop: "32px",
            textAlign: "center",
          }}
        >
          <Typography.Text style={{ fontSize: "14px", color: token.colorTextSecondary }}>
            ¿Ya tienes una cuenta?{" "}
            <Typography.Link
              onClick={() => navigate("/login")}
              style={{ fontWeight: "bold", color: token.colorSuccess }}
            >
              Inicia sesión
            </Typography.Link>
          </Typography.Text>
        </div>
      </Flex>
    </div>
  );
};

export default SignUpPage;
